// ============================================================
// PowerElectronicsSimulation.jsx
//
// Lesson: "Power Electronics & Circuit Simulation" — now built as
// two in-platform, interactive labs instead of static theory plus
// an off-platform link. Each lab follows the same shape as the
// First Lab page: pick your parts, see the wiring, hit Run, and
// watch a live animated oscilloscope trace react to your choices.
//
// This is a behavioral animation, not a full SPICE solve — but it's
// not just decorative either. The rectifier trace is driven by an
// actual charge/decay model (the output rises instantly whenever the
// rectified input exceeds it — a diode conducting — and decays at a
// rate set by the load current otherwise — a capacitor discharging
// through the load). That's the real reason ripple gets worse under
// heavier load, and the simulation shows exactly that relationship
// when you change the slider. The DC-DC lab's output settles toward
// Vout = D×Vin (buck) or Vin/(1-D) (boost) over a short transient,
// the way a real regulator's output actually behaves when duty cycle
// changes, rather than snapping instantly.
//
// Accuracy note (verified via web search, not assumed from training):
//   - NI Multisim Live reached End-of-Life on September 15, 2026 and
//     is no longer available — deliberately not recommended here.
//   - "PSpice for TI" (Cadence, sponsored by Texas Instruments) is a
//     real, currently free download aimed at analog/power design.
//   - Wokwi (free, unlimited public-project simulation) and Tinkercad
//     Circuits (Autodesk, free) are both confirmed current and active.
//   These are offered as a "go further with a real SPICE solver"
//   option — the labs on this page are the primary, in-platform
//   experience.
// ============================================================

import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    Slider,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import BoltIcon from "@mui/icons-material/Bolt";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TerminalIcon from "@mui/icons-material/Terminal";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
    danger: "#F87171",
};

const TOOLS = [
    {
        name: "PSpice for TI",
        status: "Free",
        icon: <CheckCircleIcon />,
        color: BRAND.teal,
        desc: "A free SPICE simulator from Cadence, sponsored by Texas Instruments, aimed at analog and power-supply design.",
        link: "https://www.cadence.com/en_US/home/tools/pspice-designer/pspice-for-ti.html",
    },
    {
        name: "Wokwi",
        status: "Free",
        icon: <CheckCircleIcon />,
        color: BRAND.teal,
        desc: "Browser-based Arduino/ESP32 simulator with unlimited free simulation of public projects.",
        link: "https://wokwi.com",
    },
    {
        name: "Tinkercad Circuits",
        status: "Free",
        icon: <CheckCircleIcon />,
        color: BRAND.teal,
        desc: "Autodesk's beginner-friendly breadboard simulator for visualizing physical wiring.",
        link: "https://www.tinkercad.com/circuits",
    },
    {
        name: "NI Multisim Live",
        status: "Discontinued — Sept 15, 2026",
        icon: <CancelIcon />,
        color: BRAND.danger,
        desc: "Reached end-of-life and is no longer available. If an older tutorial points you here, use PSpice for TI or Wokwi instead.",
        link: null,
    },
];

const RECTIFIER_PARTS = [
    { name: "1N4007 diode ×1 (half-wave) or ×4 / bridge module (full-wave)", price: "$0.10 – $1.50" },
    { name: "Electrolytic smoothing capacitor (470µF–1000µF)", price: "$0.20 – $0.60" },
    { name: "Load resistor (10Ω–100Ω, a few watts)", price: "$0.20 – $0.50" },
];

const DCDC_PARTS = [
    { name: "Switching regulator IC or MOSFET + driver", price: "$1 – $4" },
    { name: "Power inductor (sized for your switching frequency)", price: "$0.50 – $2" },
    { name: "Schottky diode (for a non-synchronous design)", price: "$0.20 – $0.60" },
    { name: "Output filter capacitor", price: "$0.20 – $0.60" },
];

// ── A small, shared block-flow diagram (not pin-level wiring — this
// is about circuit topology, so a left-to-right signal-flow diagram
// communicates it more clearly than a breadboard graphic would) ──
const BlockFlow = ({ stages }) => (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, py: 2 }}>
        {stages.map((s, i) => (
            <React.Fragment key={s.label}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 3,
                        textAlign: "center",
                        minWidth: 100,
                        background: s.dim ? "rgba(255,255,255,0.03)" : "rgba(218,165,32,0.1)",
                        border: `1px solid ${s.dim ? BRAND.border : BRAND.gold}`,
                        opacity: s.dim ? 0.45 : 1,
                        transition: "0.25s",
                    }}
                >
                    <Typography variant="caption" sx={{ color: s.dim ? BRAND.muted : BRAND.white, fontWeight: 700, display: "block" }}>
                        {s.label}
                    </Typography>
                    {s.sub && <Typography variant="caption" sx={{ color: BRAND.muted, fontSize: 10 }}>{s.sub}</Typography>}
                </Box>
                {i < stages.length - 1 && (
                    <Typography sx={{ color: BRAND.teal, fontSize: 20, lineHeight: 1 }}>→</Typography>
                )}
            </React.Fragment>
        ))}
    </Box>
);

// ── Shared oscilloscope animation hook ──────────────────────────
// Runs a requestAnimationFrame loop while `running` is true. `paramsRef`
// is a ref the caller keeps up to date every render, so the draw
// function always sees the latest slider/toggle values without the
// loop having to restart. `stateRef` is scratch space the draw
// function can use to carry values between frames (e.g. the
// capacitor's current charge).
const useScope = (canvasRef, running, paramsRef, stateRef, drawFrame) => {
    const rafRef = useRef(null);
    const phaseRef = useRef(0);

    useEffect(() => {
        if (!running) return undefined;
        let last = performance.now();

        const loop = (now) => {
            const dt = Math.min(now - last, 50);
            last = now;
            phaseRef.current += dt;
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext("2d");
                drawFrame(ctx, canvas.width, canvas.height, phaseRef.current, paramsRef.current, stateRef.current, dt);
            }
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [running]);
};

const drawGrid = (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 24) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
    }
    for (let y = 0; y < h; y += 24) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
    }
    ctx.strokeStyle = "rgba(255,255,255,0.15)";
    ctx.beginPath();
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
    ctx.stroke();
};

const ScopeCanvas = ({ canvasRef }) => (
    <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 3, p: 1.5, overflow: "hidden" }}>
        <canvas ref={canvasRef} width={560} height={200} style={{ width: "100%", height: "auto", display: "block" }} />
    </Box>
);

// ── LAB 1: Rectifier (AC → DC) ───────────────────────────────────

const drawRectifierFrame = (ctx, w, h, phaseMs, params, state) => {
    drawGrid(ctx, w, h);
    const bandH = h / 2;
    const { topology, smoothing, loadLevel } = params;
    const phase = phaseMs * 0.012;

    // CH1 band: AC input (gold)
    const ch1Mid = bandH / 2;
    const ch1Amp = bandH * 0.32;
    ctx.strokeStyle = BRAND.gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x <= w; x++) {
        const t = x * 0.04 + phase;
        const y = ch1Mid - Math.sin(t) * ch1Amp;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();

    // divider + label
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.beginPath();
    ctx.moveTo(0, bandH);
    ctx.lineTo(w, bandH);
    ctx.stroke();
    ctx.fillStyle = BRAND.gold;
    ctx.font = "11px monospace";
    ctx.fillText("CH1 · AC INPUT", 8, 14);

    // CH2 band: rectified / smoothed output (teal)
    const decay = loadLevel === "heavy" ? 0.05 : loadLevel === "medium" ? 0.024 : 0.009;
    let prev = state.prev ?? 0;
    const base = h - 12;
    const ch2Amp = bandH * 1.5;
    ctx.strokeStyle = BRAND.teal;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    for (let x = 0; x <= w; x++) {
        const t = x * 0.04 + phase;
        const raw = topology === "half" ? Math.max(0, Math.sin(t)) : Math.abs(Math.sin(t));
        let value = raw;
        if (smoothing) {
            if (raw > prev) prev = raw; // a conducting diode charges the cap almost instantly
            else prev = Math.max(0, prev - decay); // between peaks, the cap discharges through the load
            value = prev;
        }
        const y = base - value * ch2Amp;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    state.prev = prev;
    ctx.stroke();
    ctx.fillStyle = BRAND.teal;
    ctx.fillText("CH2 · RECTIFIED OUTPUT", 8, bandH + 14);
};

const RectifierLab = () => {
    const [topology, setTopology] = useState("full");
    const [smoothing, setSmoothing] = useState(true);
    const [loadLevel, setLoadLevel] = useState("light");
    const [running, setRunning] = useState(false);

    const canvasRef = useRef(null);
    const paramsRef = useRef({ topology, smoothing, loadLevel });
    const stateRef = useRef({});
    paramsRef.current = { topology, smoothing, loadLevel };

    useScope(canvasRef, running, paramsRef, stateRef, drawRectifierFrame);

    useEffect(() => {
        stateRef.current = {}; // reset the charge model when topology/smoothing changes so the trace doesn't jump
    }, [topology, smoothing]);

    return (
        <Paper elevation={0} sx={{ p: 4, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <AutorenewIcon sx={{ color: BRAND.gold }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Lab 1: Build a Rectifier</Typography>
            </Stack>

            <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>Choose Your Topology</Typography>
                    <RadioGroup value={topology} onChange={(e) => setTopology(e.target.value)} sx={{ mb: 2 }}>
                        <FormControlLabel
                            value="half"
                            control={<Radio sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.gold } }} />}
                            label={<Typography variant="body2" sx={{ color: BRAND.white }}>Half-Wave (1 diode)</Typography>}
                        />
                        <FormControlLabel
                            value="full"
                            control={<Radio sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.gold } }} />}
                            label={<Typography variant="body2" sx={{ color: BRAND.white }}>Full-Wave Bridge (4 diodes)</Typography>}
                        />
                    </RadioGroup>

                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                        <Switch checked={smoothing} onChange={(e) => setSmoothing(e.target.checked)} sx={{ "& .Mui-checked": { color: BRAND.teal }, "& .Mui-checked + .MuiSwitch-track": { bgcolor: BRAND.teal } }} />
                        <Typography variant="body2" sx={{ color: BRAND.white }}>Smoothing Capacitor</Typography>
                    </Stack>

                    <Typography variant="caption" sx={{ color: BRAND.muted }}>Load Current</Typography>
                    <ToggleButtonGroup
                        value={loadLevel}
                        exclusive
                        onChange={(e, v) => v && setLoadLevel(v)}
                        size="small"
                        sx={{ display: "flex", mb: 3 }}
                    >
                        {["light", "medium", "heavy"].map((lvl) => (
                            <ToggleButton
                                key={lvl}
                                value={lvl}
                                sx={{ flex: 1, color: BRAND.muted, borderColor: BRAND.border, "&.Mui-selected": { bgcolor: "rgba(218,165,32,0.15)", color: BRAND.gold } }}
                            >
                                {lvl}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>What You'll Buy</Typography>
                    <Stack spacing={0.75} sx={{ mb: 3 }}>
                        {RECTIFIER_PARTS.map((p) => (
                            <Stack key={p.name} direction="row" justifyContent="space-between" spacing={2}>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{p.name}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.teal, fontWeight: 700, whiteSpace: "nowrap" }}>{p.price}</Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <BlockFlow
                        stages={[
                            { label: "AC Source" },
                            { label: topology === "half" ? "1 Diode" : "4-Diode Bridge" },
                            { label: "Capacitor", dim: !smoothing },
                            { label: "Load" },
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={7}>
                    <ScopeCanvas canvasRef={canvasRef} />
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={running ? <StopIcon /> : <PlayArrowIcon />}
                            onClick={() => setRunning((r) => !r)}
                            sx={{ bgcolor: running ? BRAND.danger : BRAND.gold, color: BRAND.bg, fontWeight: 800 }}
                        >
                            {running ? "Stop" : "Run Simulation"}
                        </Button>
                    </Stack>
                    <Typography variant="caption" sx={{ color: BRAND.muted, display: "block", mt: 2 }}>
                        {topology === "half"
                            ? "Half-wave: the output drops to zero for an entire half-cycle — notice the gap."
                            : "Full-wave bridge: both halves of the AC cycle get redirected the same way, so the output never fully drops to zero."}
                        {smoothing && " The capacitor fills in the gaps — raise the load current to see the ripple get worse, exactly like a real supply under heavier draw."}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

// ── LAB 2: DC-DC Converter (Buck / Boost) ────────────────────────

const drawDcDcFrame = (ctx, w, h, phaseMs, params, state, dt) => {
    drawGrid(ctx, w, h);
    const bandH = h / 2;
    const { topology, duty, vin } = params;
    const d = duty / 100;
    const period = 50;

    // CH1 band: PWM gate signal (gold)
    ctx.strokeStyle = BRAND.gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let x = 0; x <= w; x++) {
        const posInPeriod = ((x + phaseMs * 0.15) % period) / period;
        const y = posInPeriod < d ? bandH * 0.25 : bandH * 0.75;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.strokeStyle = "rgba(255,255,255,0.12)";
    ctx.beginPath();
    ctx.moveTo(0, bandH);
    ctx.lineTo(w, bandH);
    ctx.stroke();
    ctx.fillStyle = BRAND.gold;
    ctx.font = "11px monospace";
    ctx.fillText("CH1 · SWITCH GATE (PWM)", 8, 14);

    // CH2 band: output voltage settling toward target, with ripple (teal)
    const maxScale = vin * (topology === "buck" ? 1.2 : 6);
    const target = topology === "buck" ? vin * d : Math.min(vin / Math.max(1 - d, 0.05), vin * 5.5);
    let current = state.vout ?? target;
    current += (target - current) * Math.min(dt * 0.0035, 0.25);
    state.vout = current;

    const base = h - 12;
    const ch2Amp = bandH * 1.5;
    ctx.strokeStyle = BRAND.teal;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    for (let x = 0; x <= w; x++) {
        const ripple = Math.sin((x + phaseMs * 0.15) * 0.25) * (maxScale * 0.015);
        const value = Math.max(0, current + ripple);
        const norm = Math.min(value / maxScale, 1);
        const y = base - norm * ch2Amp;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.fillStyle = BRAND.teal;
    ctx.fillText(`CH2 · OUTPUT  ≈ ${current.toFixed(2)}V`, 8, bandH + 14);
};

const DcDcLab = () => {
    const [topology, setTopology] = useState("buck");
    const [vin, setVin] = useState(12);
    const [duty, setDuty] = useState(40);
    const [running, setRunning] = useState(false);

    const canvasRef = useRef(null);
    const paramsRef = useRef({ topology, vin, duty });
    const stateRef = useRef({});
    paramsRef.current = { topology, vin, duty };

    useScope(canvasRef, running, paramsRef, stateRef, drawDcDcFrame);

    useEffect(() => {
        stateRef.current = {}; // reset the settling model on topology/Vin change
    }, [topology]);

    const estimatedVout =
        topology === "buck" ? (vin * (duty / 100)).toFixed(2) : Math.min(vin / Math.max(1 - duty / 100, 0.05), vin * 5.5).toFixed(2);

    return (
        <Paper elevation={0} sx={{ p: 4, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                <BoltIcon sx={{ color: BRAND.gold }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Lab 2: Build a DC-DC Converter</Typography>
            </Stack>

            <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>Choose Your Topology</Typography>
                    <RadioGroup value={topology} onChange={(e) => setTopology(e.target.value)} sx={{ mb: 2 }}>
                        <FormControlLabel
                            value="buck"
                            control={<Radio sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.gold } }} />}
                            label={<Typography variant="body2" sx={{ color: BRAND.white }}>Buck (Step-Down)</Typography>}
                        />
                        <FormControlLabel
                            value="boost"
                            control={<Radio sx={{ color: BRAND.muted, "&.Mui-checked": { color: BRAND.gold } }} />}
                            label={<Typography variant="body2" sx={{ color: BRAND.white }}>Boost (Step-Up)</Typography>}
                        />
                    </RadioGroup>

                    <Typography variant="caption" sx={{ color: BRAND.muted }}>Input Voltage (Vin): {vin}V</Typography>
                    <Slider value={vin} min={3} max={24} step={0.5} onChange={(e, v) => setVin(v)} sx={{ color: BRAND.teal, mb: 2 }} />

                    <Typography variant="caption" sx={{ color: BRAND.muted }}>Duty Cycle (D): {duty}%</Typography>
                    <Slider value={duty} min={1} max={95} onChange={(e, v) => setDuty(v)} sx={{ color: BRAND.gold, mb: 3 }} />

                    <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 3, p: 2, textAlign: "center", mb: 3 }}>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>Target Vout</Typography>
                        <Typography variant="h4" sx={{ color: BRAND.teal, fontWeight: 900 }}>{estimatedVout}V</Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>
                            {topology === "buck" ? "Vout = D × Vin" : "Vout = Vin / (1 − D)"}
                        </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ color: BRAND.white, fontWeight: 700, mb: 1 }}>What You'll Buy</Typography>
                    <Stack spacing={0.75} sx={{ mb: 3 }}>
                        {DCDC_PARTS.map((p) => (
                            <Stack key={p.name} direction="row" justifyContent="space-between" spacing={2}>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>{p.name}</Typography>
                                <Typography variant="caption" sx={{ color: BRAND.teal, fontWeight: 700, whiteSpace: "nowrap" }}>{p.price}</Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <BlockFlow
                        stages={[
                            { label: "DC Input", sub: `${vin}V` },
                            { label: "Switch", sub: `${duty}% duty` },
                            { label: "Inductor" },
                            { label: "Load" },
                        ]}
                    />
                </Grid>

                <Grid item xs={12} md={7}>
                    <ScopeCanvas canvasRef={canvasRef} />
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={running ? <StopIcon /> : <PlayArrowIcon />}
                            onClick={() => setRunning((r) => !r)}
                            sx={{ bgcolor: running ? BRAND.danger : BRAND.gold, color: BRAND.bg, fontWeight: 800 }}
                        >
                            {running ? "Stop" : "Run Simulation"}
                        </Button>
                    </Stack>
                    <Typography variant="caption" sx={{ color: BRAND.muted, display: "block", mt: 2 }}>
                        Drag the duty-cycle slider while it's running — the output doesn't jump instantly, it settles
                        toward the new target over a short transient, the way a real converter's output capacitor
                        actually behaves.
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

// ── Tool card (verified-accurate external simulators) ────────────

const ToolCard = ({ name, status, icon, color, desc, link }) => (
    <Paper elevation={0} sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 4, height: "100%" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
            <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 700 }}>{name}</Typography>
        </Stack>
        <Chip size="small" label={status} sx={{ bgcolor: `${color}26`, color, fontWeight: 700, mb: 1.5 }} />
        <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6, mb: link ? 2 : 0 }}>{desc}</Typography>
        {link && (
            <Button
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                component="a"
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: BRAND.teal }}
            >
                Visit
            </Button>
        )}
    </Paper>
);

// ── Main Page ───────────────────────────────────────────────────

const PowerElectronicsSimulation = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Embedded Engineering · Power Electronics"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Power <span style={{ color: BRAND.teal }}>Electronics</span> Lab
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Every board you've built so far assumed clean DC power. Pick your parts, wire up the
                    topology, hit run, and watch the waveform on a live scope — right here, no external tool
                    required.
                </Typography>
            </Box>

            <Stack spacing={5} sx={{ mb: 10 }}>
                <RectifierLab />
                <DcDcLab />
            </Stack>

            {/* SIMULATION TOOLS — go further */}
            <Box sx={{ mb: 10 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <MonitorHeartIcon sx={{ color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>Want a Real SPICE Solve?</Typography>
                </Stack>
                <Typography sx={{ color: BRAND.muted, mb: 4, maxWidth: 800 }}>
                    The labs above are for building intuition fast. For a true numerical solve of the same
                    circuits, here's what's actually free and active right now — this space moves fast, and
                    tools in older tutorials sometimes disappear.
                </Typography>
                <Grid container spacing={3}>
                    {TOOLS.map((t) => (
                        <Grid item xs={12} sm={6} md={3} key={t.name}>
                            <ToolCard {...t} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready for Real Hardware?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                    Take what you learned here back to the Smart RFID Lock and power it from a real rectified,
                    regulated supply instead of a USB cable.
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                    <ShoppingCartIcon sx={{ color: BRAND.gold }} />
                    <Typography variant="caption" sx={{ color: BRAND.muted }}>Every part on this page is a real, buyable component.</Typography>
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default PowerElectronicsSimulation;

// ------------------------------------------------------------------
// ACTION ITEM for Hazzan: new standalone page, no route yet. Add to
// App.js, e.g.:
//   import PowerElectronicsSimulation from "./pages/PowerElectronicsSimulation";
//   <Route path="/power-electronics-simulation" element={<RequireAuth user={user}><PowerElectronicsSimulation/></RequireAuth>}/>
// and add it to whichever dashboard lists the Embedded Systems track,
// positioned after First Lab.
// ------------------------------------------------------------------
